<template>
    <v-container>
        <v-row v-if="country === 'VEN'" justify="center">
            <v-col cols="12" v-if="loading === true">
                <v-skeleton-loader class="py-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line"/>
            </v-col>
            <template v-else>
                <v-col cols="12" lg="7">
                    <v-card class="ven-hero" height="100%">
                        <v-card-text>
                            <div class="d-flex flex-wrap align-center">
                                <div class="flex-grow-1">
                                    <div class="ven-kicker">
                                        <v-icon small color="primary">mdi-chart-timeline-variant</v-icon>
                                        Referencia principal
                                    </div>
                                    <h1 class="ven-title">Dolar Digital</h1>
                                </div>
                                <v-chip small outlined :color="digitalRate && digitalRate.status === 'ok' ? 'success' : 'warning'" class="my-1">
                                    {{ digitalRate && digitalRate.status === 'ok' ? 'Actualizado' : 'No disponible' }}
                                </v-chip>
                            </div>
                            <div class="ven-rate">
                                {{ digitalRate ? formatMoney(digitalRate.value, digitalRate.symbol, digitalRate.to) : formatMoney(0, 'Bs. ', 'VES') }}
                            </div>
                            <div class="ven-meta">
                                <v-icon small>mdi-calendar</v-icon>
                                {{ digitalRate && digitalRate.date ? digitalRate.date : 'Sin fecha disponible' }}
                            </div>
                            <v-divider class="my-4"></v-divider>
                            <div class="ven-actions">
                                <v-btn color="primary" to="/calculator" class="my-1">
                                    <v-icon left small>mdi-calculator</v-icon>
                                    Calculadora
                                </v-btn>
                                <v-btn outlined color="primary" to="/history" class="my-1">
                                    <v-icon left small>mdi-clock</v-icon>
                                    Histórico
                                </v-btn>
                                <v-btn text color="primary" :disabled="!digitalRate" @click="copyRate(digitalRate)" class="my-1">
                                    <v-icon left small>mdi-content-copy</v-icon>
                                    Copiar
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" lg="5">
                    <v-card height="100%">
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                            <v-icon color="bg">mdi-bank</v-icon> Referencia Oficial
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                            <div class="ven-market-row">
                                <div>
                                    <div class="ven-market-label">Dolar Oficial BCV</div>
                                    <div class="ven-market-value">
                                        {{ officialRate ? formatMoney(officialRate.value, officialRate.symbol, officialRate.to) : formatMoney(0, 'Bs. ', 'VES') }}
                                    </div>
                                </div>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon color="primary" :disabled="!officialRate" @click="copyRate(officialRate)" v-bind="attrs" v-on="on">
                                            <v-icon>mdi-content-copy</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Copiar BCV</span>
                                </v-tooltip>
                            </div>
                            <v-divider class="my-3"></v-divider>
                            <div class="ven-market-row">
                                <div>
                                    <div class="ven-market-label">Diferencia digital frente al BCV</div>
                                    <div class="ven-market-value primary--text">
                                        {{ formattedDigitalGap }}
                                    </div>
                                </div>
                                <v-chip outlined color="primary">
                                    {{ formattedDigitalGapPercent }}
                                </v-chip>
                            </div>
                            <v-alert dense outlined type="info" class="mt-4 mb-0">
                                BCV queda como referencia oficial; el valor digital se prioriza para el uso diario.
                            </v-alert>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12">
                    <v-card>
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                            <v-icon color="bg">mdi-chart-bar</v-icon> Comparativa en Bolívares
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                            <div class="ven-compare">
                                <div class="ven-compare__summary">
                                    <div>
                                        <div class="ven-market-label">Brecha digital</div>
                                        <div class="ven-compare__gap">{{ formattedDigitalGap }}</div>
                                    </div>
                                    <v-chip outlined :color="digitalGap >= 0 ? 'primary' : 'error'">
                                        {{ formattedDigitalGapPercent }}
                                    </v-chip>
                                </div>
                                <div class="ven-compare__rows">
                                    <div
                                        v-for="rate in availableRates"
                                        :key="'compare-' + rate.key"
                                        class="ven-compare__row"
                                    >
                                        <div class="ven-compare__top">
                                            <span>{{ rate.label || rate.title }}</span>
                                            <strong>{{ formatMoney(rate.value, rate.symbol, rate.to) }}</strong>
                                        </div>
                                        <div class="ven-compare__track">
                                            <div class="ven-compare__fill" :style="{ width: getRateBarHeight(rate.value) + '%' }"></div>
                                        </div>
                                        <div class="ven-compare__foot">
                                            <span>$100 USD</span>
                                            <span>{{ formatVesConversion(100, rate) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12">
                    <v-card>
                        <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text">
                            <v-icon color="bg">mdi-table</v-icon> Montos Frecuentes
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">Monto</th>
                                        <th class="text-right">Digital</th>
                                        <th class="text-right">Oficial BCV</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="amount in venezuelaAmounts" :key="'ven-amount-' + amount">
                                        <td>{{ formatMoney(amount, '$', 'USD', 0) }}</td>
                                        <td class="text-right">{{ formatVesConversion(amount, digitalRate) }}</td>
                                        <td class="text-right">{{ formatVesConversion(amount, officialRate) }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </template>
        </v-row>
        <v-row v-else justify="center">
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-flex align-center py-1 font-weight-bold card bg--text" style="word-break: normal">
                        <span class="flex-grow-1 text-center">
                            <v-icon color="bg">mdi-calendar</v-icon> Dólar (TRM) de Hoy
                        </span>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon color="primary" @click="copyTodayRate" v-bind="attrs" v-on="on">
                                    <v-icon>mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                            <span>Copiar TRM</span>
                        </v-tooltip>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="text-center text-h3 font-weight-bold pb-1 bg--text">
                        {{ formattedToday }}
                    </v-card-text>
                    <v-card-text :class="'text-center text-h6 py-1 ' + value.color + '--text'">
                        {{ formattedDifference }} ({{ formattedPercentage }}) <v-icon :color="value.color">mdi-menu-{{ value.icon }}</v-icon>
                    </v-card-text>
                    <v-card-text class="text-center py-1">
                        <v-chip small outlined color="primary" class="mx-1 my-1">
                            Ayer {{ formatMoney(value.yesterday, currencyResult.symbol, currencyResult.code) }}
                        </v-chip>
                        <v-chip small outlined color="primary" class="mx-1 my-1" v-if="Number(value.tomorrow) > 0">
                            Mañana {{ formatMoney(value.tomorrow, currencyResult.symbol, currencyResult.code) }}
                        </v-chip>
                    </v-card-text>
                    <v-card-actions class="justify-center pt-1 pb-4">
                        <v-btn small outlined color="primary" @click="copyTodayRate">
                            <v-icon left small>mdi-content-copy</v-icon>
                            Copiar TRM
                        </v-btn>
                        <v-btn small color="primary" to="/calculator">
                            <v-icon left small>mdi-calculator</v-icon>
                            Calculadora
                        </v-btn>
                        <v-btn small outlined color="primary" to="/history">
                            <v-icon left small>mdi-clock</v-icon>
                            Histórico
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-text</v-icon> RESUMEN
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="text-center pb-1">
                        Esta es la TRM vigente del {{ new Date() | moment("dddd LL") }}
                    </v-card-text>
                    <v-card-text class="text-center pt-1">
                        {{ currency.symbol }}1.00 {{ currency.code }} (un dólar estadounidense) equivale a {{ formattedToday }} ({{ toWords(value.today) }} pesos colombianos)
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="loading === true">
                <v-skeleton-loader class="py-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line"/>
            </v-col>
            <v-col v-for="i in items" :key="i.from + '-' + i.to" cols="12" md="6" v-else>
                <v-card>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text">
                        Tabla de {{ i.from }} a {{ i.to }}
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-list class="px-2" dense>
                        <template v-for="v in i.values">
                            <v-list-item :key="'V' + v" class="trunck-text" style="height: 30px" dense>
                                <v-list-item-content class="d-block text-right">
                                    {{ i.fromSymbol + Number(v).toLocaleString("en-US") + ' ' + i.from }}
                                </v-list-item-content>
                                <v-list-item-icon class="d-block text-center px-2">
                                    <v-icon small>mdi-arrow-right-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="d-block">
                                    {{ formatConversion(i, v) }}
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider v-if="v !== i.values[i.values.length-1]"></v-divider>
                        </template>
                    </v-list>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { toWords } from "n2words/es";

    export default {
        name: "Home",
        props: [ 'country', 'item', 'load', 'rates' ],
        components: {
            VBoilerplate: {
                functional: true,

                render(h, {data, props, children}) {
                    return h('v-skeleton-loader', {
                        ...data,
                        props: {
                            boilerplate: true,
                            elevation: 0,
                            ...props,
                        },
                    }, children)
                },
            }
        },
        data: () => ({
            loading: true,
            value: {
                yesterday: 0,
                today: 0,
                tomorrow: 0,
                difference: 0,
                symbol: '+',
                percentage: 0,
                color: 'success',
                icon: 'up'
            },
            currency: {
                symbol: '$',
                code: 'USD'
            },
            currencyResult: {
                symbol: '$',
                code: 'COP'
            },
            items: [
                {
                    from: 'USD',
                    fromSymbol: '$',
                    to: 'COP',
                    toSymbol: '$',
                    values: [ 1, 2, 5, 10, 15, 20, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000 ]
                }, {
                    from: 'COP',
                    fromSymbol: '$',
                    to: 'USD',
                    toSymbol: '$',
                    values: [ 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000, 2000000, 5000000, 10000000, 20000000, 50000000 ]
                },
            ],
            venezuelaAmounts: [ 1, 5, 10, 20, 50, 100, 200, 500, 1000, 5000, 10000 ],
        }),
	        mounted() {
	            this.getData();
	        },
	        watch: {
            country() {
                this.getData();
            },
            load() {
                this.loading = this.load;
            },
            item() {
                this.value = this.item;
            },
        },
	        computed: {
	            availableRates() {
	                const priority = {
	                    'digital-usd': 0,
	                    'bcv-usd': 1,
	                };

	                return (this.rates || [])
	                    .filter(rate => Number(rate.value) > 0)
	                    .sort((a, b) => (priority[a.key] || 9) - (priority[b.key] || 9));
	            },
	            officialRate() {
	                return this.availableRates.find(rate => rate.key === 'bcv-usd') || null;
	            },
	            digitalRate() {
	                return this.availableRates.find(rate => rate.key === 'digital-usd') || null;
	            },
	            digitalGap() {
	                if (!this.officialRate || !this.digitalRate) {
	                    return 0;
	                }

	                return Number(this.digitalRate.value) - Number(this.officialRate.value);
	            },
	            digitalGapPercent() {
	                if (!this.officialRate || !Number(this.officialRate.value)) {
	                    return 0;
	                }

	                return (this.digitalGap * 100) / Number(this.officialRate.value);
	            },
	            formattedDigitalGap() {
	                const symbol = this.digitalGap < 0 ? '-' : '+';

	                return symbol + this.formatMoney(Math.abs(this.digitalGap), 'Bs. ', 'VES');
	            },
	            formattedDigitalGapPercent() {
	                const symbol = this.digitalGapPercent < 0 ? '-' : '+';

	                return symbol + Number(Math.abs(this.digitalGapPercent) || 0).toLocaleString("en-US", {
	                    minimumFractionDigits: 2,
	                    maximumFractionDigits: 2
	                }) + '%';
	            },
	            maxRateValue() {
	                return this.availableRates.reduce((max, rate) => Math.max(max, Number(rate.value)), 0);
	            },
	            trendSymbol() {
	                return Number(this.value.difference) < 0 ? '-' : '+';
	            },
	            formattedToday() {
	                return this.formatMoney(this.value.today, this.currencyResult.symbol, this.currencyResult.code);
	            },
	            formattedDifference() {
	                return this.trendSymbol + this.formatMoney(Math.abs(this.value.difference), this.currencyResult.symbol, this.currencyResult.code);
	            },
	            formattedPercentage() {
	                return this.trendSymbol + Number(Math.abs(this.value.percentage) || 0).toLocaleString("en-US", {
	                    minimumFractionDigits: 2,
	                    maximumFractionDigits: 2
	                }) + '%';
	            }
	        },
	        methods: {
	            toWords,
	            formatMoney(value, symbol, code, decimals = 2) {
	                return symbol + Number(value || 0).toLocaleString("en-US", {
	                    minimumFractionDigits: decimals,
	                    maximumFractionDigits: decimals
	                }) + ' ' + code;
	            },
	            formatConversion(item, amount) {
	                if (!Number(this.value.today)) {
	                    return this.formatMoney(0, item.toSymbol, item.to);
	                }

	                const result = item.to === 'COP' ? amount * this.value.today : amount / this.value.today;
	                const decimals = item.to === 'COP' && result > 999999 ? 0 : 2;

	                return this.formatMoney(result, item.toSymbol, item.to, decimals);
	            },
	            formatVesConversion(amount, rate) {
	                if (!rate || !Number(rate.value)) {
	                    return this.formatMoney(0, 'Bs. ', 'VES');
	                }

	                return this.formatMoney(Number(amount) * Number(rate.value), 'Bs. ', 'VES');
	            },
	            copyTodayRate() {
	                const text = 'TRM de hoy: ' + this.formattedToday + '. Variación: ' + this.formattedDifference + ' (' + this.formattedPercentage + ').';

	                if (navigator.clipboard && navigator.clipboard.writeText) {
	                    navigator.clipboard.writeText(text).then(() => {
	                        this.$toast.success('TRM copiada.', { icon: 'mdi-check-circle' });
	                    }).catch(() => {
	                        this.copyWithFallback(text);
	                    });
	                    return;
	                }

	                this.copyWithFallback(text);
	            },
	            copyRate(rate) {
		                const text = rate.title + ': ' + this.formatMoney(rate.value, rate.symbol, rate.to) + '. Referencia: ' + rate.source + '.';

	                if (navigator.clipboard && navigator.clipboard.writeText) {
	                    navigator.clipboard.writeText(text).then(() => {
	                        this.$toast.success('Tasa copiada.', { icon: 'mdi-check-circle' });
	                    }).catch(() => {
	                        this.copyWithFallback(text);
	                    });
	                    return;
	                }

	                this.copyWithFallback(text);
	            },
	            copyWithFallback(text) {
	                const input = document.createElement('textarea');
	                input.value = text;
	                document.body.appendChild(input);
	                input.select();
	                document.execCommand('copy');
	                document.body.removeChild(input);
	                this.$toast.success('Copiado.', { icon: 'mdi-check-circle' });
	            },
	            getRateBarHeight(value) {
	                if (!this.maxRateValue) {
	                    return 0;
	                }

	                return Math.max(12, (Number(value) / this.maxRateValue) * 100);
	            },
	            getData() {
	                if (this.load === false) {
	                    this.value = this.item;
	                    this.loading = this.load;
	                }
	            }
	        }
	    }
</script>

<style scoped>
.trunck-text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.ven-hero {
    border-top: 4px solid var(--v-primary-base);
}
.ven-kicker {
    align-items: center;
    display: flex;
    font-size: 0.85rem;
    font-weight: 700;
    gap: 6px;
    letter-spacing: 0;
    text-transform: uppercase;
}
.ven-title {
    color: var(--v-bg-base);
    font-size: 1.55rem;
    font-weight: 800;
    line-height: 1.2;
    margin: 8px 0 0;
}
.ven-rate {
    color: var(--v-bg-base);
    font-size: 2.45rem;
    font-weight: 800;
    line-height: 1.15;
    margin-top: 16px;
    word-break: normal;
}
.ven-meta {
    align-items: center;
    color: var(--v-bg-base);
    display: flex;
    font-size: 0.95rem;
    gap: 6px;
    margin-top: 8px;
    opacity: 0.72;
}
.ven-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.ven-market-row {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
}
.ven-market-label {
    color: var(--v-bg-base);
    font-size: 0.85rem;
    font-weight: 700;
    opacity: 0.72;
}
.ven-market-value {
    color: var(--v-bg-base);
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1.25;
    margin-top: 4px;
}
.ven-compare {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ven-compare__summary {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
}
.ven-compare__gap {
    color: var(--v-bg-base);
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.25;
}
.ven-compare__rows {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.ven-compare__row {
    border: 1px solid var(--v-secondary-base);
    border-radius: 8px;
    padding: 12px;
}
.ven-compare__top,
.ven-compare__foot {
    align-items: center;
    color: var(--v-bg-base);
    display: flex;
    gap: 12px;
    justify-content: space-between;
}
.ven-compare__top {
    font-weight: 800;
}
.ven-compare__foot {
    font-size: 0.88rem;
    margin-top: 8px;
    opacity: 0.76;
}
.ven-compare__track {
    background: var(--v-secondary-base);
    border-radius: 6px;
    height: 10px;
    margin-top: 10px;
    overflow: hidden;
}
.ven-compare__fill {
    background: var(--v-primary-base);
    border-radius: 6px;
    height: 100%;
    min-width: 12%;
    transition: width 0.2s ease;
}
@media (max-width: 600px) {
    .ven-rate {
        font-size: 1.85rem;
    }

    .ven-market-value {
        font-size: 1.15rem;
    }
}
</style>
